import type { CustomIcon } from '../../interfaces/CustomIcon';

export interface TableColumnModel {
  id: number;
  header?: string;
  subTitle?: string;
  key: string;
  type: TableColumnType;
  icon?: CustomIcon;
  sortable: boolean;
  frozen?: boolean;
  /**
   * Left out of the grid and its skeleton at or below `$bp-mobile`, where a phone row has room for
   * the columns that name the row and act on it. Read through `useIsMobile`, so it follows a
   * rotation.
   */
  mobileHidden?: boolean;
  identifier?: string;
  value?: string | number;
  exportFormNames?: string;

  /*
   * The fields below are what `Table.vue` needs to draw a cell of `type` without the caller writing
   * a `#bodyContent` branch for it. The store that declares the column sets them, because the store
   * is what knows the row shape.
   *
   * Two lists use them. `studyGroups.ts` declares every column and GroupListTab writes no slot.
   * `forms.ts` declares Order, both dates and Fields for Table to draw, and FormList keeps a partial
   * slot for the three cells that carry behaviour (a link, a tooltip, buttons). A partial slot's
   * `v-if` chain misses the other columns, and Table draws those from their type. The other lists
   * still write a slot for every column and adopt these one list at a time.
   */

  /** `DATE` only. Defaults to `DATE_FORMAT_2`, which is what every list but the group list uses. */
  dateFormat?: string;
  /**
   * `TEXT_WITH_ICON` only: the row field holding this cell's icon, where the icon varies row by row
   * rather than being the column's. `Archive.vue` reads `data.icon`; the rest use `col.icon`.
   */
  iconKey?: string;
  /** `USER` only: the row field holding the avatar image. `creatorAvatar` on a case, `avatar` on a user. */
  avatarKey?: string;
  /** `USER` only: the row field holding the address shown under the name. The user lists show one. */
  emailKey?: string;
  /** `USER` only. 28px in every list but the user list, which draws a 44px row. */
  avatarSize?: number;
  /**
   * What to draw where the row has no value. "System Generated" on a case's creator, which is a
   * real statement about the case and not a placeholder.
   */
  emptyText?: string;
}

// eslint-disable-next-line no-shadow
export enum TableColumnType {
  TEXT,
  USER,
  TEXT_WITH_ICON,
  BUTTON_WITH_ICON,
  SQUARE_BUTTON_WITH_ICON,
  SELECT,
  PIPELINE,
  /**
   * Appended rather than slotted in beside `TEXT`: these are numeric enum members, so inserting one
   * renumbers every member after it.
   *
   * Table draws it in `dateFormat`. The group list and the forms list declare their dates this
   * way. The other lists still declare them `TEXT` and reformat them in their own slot, by matching
   * `col.key` against `'createdAt'`.
   */
  DATE
}
